import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import logo from '../../assets/logo-getit.png';
import BrandLogo from '../common/BrandLogo';

const ProductOptionsModal = ({ product, onClose, onSubmit }) => {
    const [selectedQuantities, setSelectedQuantities] = useState({});
    const [expandedSections, setExpandedSections] = useState({});
    const [categoryErrors, setCategoryErrors] = useState({});

    const groupedAttributes = useMemo(() => {
        if (!product.custom_attributes || product.custom_attributes.length === 0) {
            return {};
        }

        const groups = {};
        product.custom_attributes.forEach(attr => {
            const categoryName = attr.category_name || 'Sin Categoria';
            if (!groups[categoryName]) {
                groups[categoryName] = {
                    category_id: attr.category_id,
                    category_name: categoryName,
                    attributes: []
                };
            }
            groups[categoryName].attributes.push(attr);
        });

        return groups;
    }, [product.custom_attributes]);

    const categoryLimits = useMemo(() => {
        return product.category_limits || {};
    }, [product.category_limits]);

    const calculateCategoryTotal = useCallback((categoryId) => {
        if (!product.custom_attributes) return 0;
        let total = 0;
        product.custom_attributes.forEach(attr => {
            if (attr.category_id === categoryId) {
                total += (selectedQuantities[attr.id] || 0);
            }
        });
        return total;
    }, [selectedQuantities, product.custom_attributes]);

    const getCategoryMinLimit = useCallback((categoryId) => {
        return categoryLimits[categoryId]?.min_quantity || 0;
    }, [categoryLimits]);

    const getCategoryMaxLimit = useCallback((categoryId) => {
        return categoryLimits[categoryId]?.max_quantity || 0;
    }, [categoryLimits]);

    const totalPrice = useMemo(() => {
        let price = product.price;
        if (product.custom_attributes && product.custom_attributes.length > 0) {
            product.custom_attributes.forEach(attr => {
                const qty = selectedQuantities[attr.id] || 0;
                const defaultQty = attr.is_default ? attr.default_qty : 0;
                const extraQty = Math.max(0, qty - defaultQty);
                if (extraQty > 0) {
                    price += attr.price * extraQty;
                }
            });
        }
        return price;
    }, [selectedQuantities, product]);

    useEffect(() => {
        const initialQuantities = {};
        const initialExpanded = {};

        if (product.custom_attributes && product.custom_attributes.length > 0) {
            product.custom_attributes.forEach(attr => {
                if (attr.is_default && attr.default_qty > 0) {
                    initialQuantities[attr.id] = attr.default_qty;
                } else {
                    initialQuantities[attr.id] = 0;
                }
            });
        }

        Object.keys(groupedAttributes).forEach(categoryName => {
            const categoryData = groupedAttributes[categoryName];
            if (categoryData.attributes.length > 1) {
                initialExpanded[categoryName] = false;
            }
        });

        setSelectedQuantities(initialQuantities);
        setExpandedSections(initialExpanded);
        setCategoryErrors({});
    }, [product, groupedAttributes]);

    const toggleSection = useCallback((categoryName) => {
        setExpandedSections(prev => ({
            ...prev,
            [categoryName]: !prev[categoryName]
        }));
    }, []);

    const handleSelectSingle = useCallback((attrId, categoryId, categoryData) => {
        setSelectedQuantities(prev => {
            const newQuantities = { ...prev };
            categoryData.attributes.forEach(attr => {
                newQuantities[attr.id] = 0;
            });
            newQuantities[attrId] = 1;
            return newQuantities;
        });
        setCategoryErrors({});
    }, []);

    const handleIncrement = useCallback((attrId, maxQty, categoryId) => {
        setSelectedQuantities(prev => {
            const currentQty = prev[attrId] || 0;
            if (maxQty > 0 && currentQty >= maxQty) return prev;

            const categoryLimit = getCategoryMaxLimit(categoryId);
            if (categoryLimit > 0) {
                const currentCategoryTotal = calculateCategoryTotal(categoryId);
                if (currentCategoryTotal >= categoryLimit) return prev;
            }

            return {
                ...prev,
                [attrId]: currentQty + 1
            };
        });
    }, [getCategoryMaxLimit, calculateCategoryTotal]);

    const handleDecrement = useCallback((attrId, minQty) => {
        setSelectedQuantities(prev => {
            const currentQty = prev[attrId] || 0;
            if (currentQty <= 0) return prev;
            const newQty = currentQty - 1;
            if (newQty < minQty) return prev;
            return {
                ...prev,
                [attrId]: newQty
            };
        });
    }, []);

    const validateAllCategories = useCallback(() => {
        const errors = {};
        let hasErrors = false;

        Object.keys(groupedAttributes).forEach(categoryName => {
            const categoryData = groupedAttributes[categoryName];
            const categoryTotal = calculateCategoryTotal(categoryData.category_id);
            const minRequired = getCategoryMinLimit(categoryData.category_id);

            if (minRequired > 0 && categoryTotal < minRequired) {
                errors[categoryData.category_id] = `Selecciona al menos ${minRequired}`;
                hasErrors = true;
            }
        });

        setCategoryErrors(errors);
        return !hasErrors;
    }, [groupedAttributes, calculateCategoryTotal, getCategoryMinLimit]);

    const handleAddToCart = useCallback(() => {
        if (!validateAllCategories()) return;

        const attributesDescription = [];
        const attribute_lines = [];

        if (product.custom_attributes && product.custom_attributes.length > 0) {
            product.custom_attributes.forEach(attr => {
                const qty = selectedQuantities[attr.id] || 0;
                if (qty > 0) {
                    attributesDescription.push(`${attr.name} x${qty}`);
                    attribute_lines.push({
                        attribute_id: attr.id,
                        name: attr.name,
                        attribute_name: attr.category_name || '',
                        category_id: attr.category_id || null,
                        qty: qty,
                        price: attr.price || 0,
                        is_addition: attr.price > 0
                    });
                }
            });
        }

        onSubmit(selectedQuantities, totalPrice, attributesDescription.join(', '), attribute_lines);
        onClose();
    }, [validateAllCategories, product, selectedQuantities, totalPrice, onSubmit, onClose]);

    const getImageSrc = () => {
        if (product.image_base64) {
            return `data:image/jpeg;base64,${product.image_base64}`;
        }
        if (product.id) {
            return `http://localhost:9000/images/products/${product.id}`;
        }
        return null;
    };

    const getSelectedLabel = (categoryData) => {
        const selectedItems = [];
        categoryData.attributes.forEach(attr => {
            const qty = selectedQuantities[attr.id] || 0;
            if (qty > 0) {
                selectedItems.push(`${attr.name}${qty > 1 ? ` x${qty}` : ''}`);
            }
        });
        return selectedItems.length > 0 ? selectedItems.join(', ') : 'Seleccionar';
    };

    const renderAttributeSection = (categoryName, categoryData) => {
        const isSingleOption = categoryData.attributes.length === 1;
        const minRequired = getCategoryMinLimit(categoryData.category_id);
        const maxLimit = getCategoryMaxLimit(categoryData.category_id);
        const isSingleSelection = maxLimit === 1;
        const isExpanded = expandedSections[categoryName];
        const hasError = categoryErrors[categoryData.category_id];

        if (isSingleOption) {
            const attr = categoryData.attributes[0];
            const qty = selectedQuantities[attr.id] || 0;

            return (
                <div key={categoryName} className="attribute-row-direct">
                    <div className="attribute-row-info">
                        <div className="attribute-row-name">{attr.name}</div>
                        {attr.price > 0 && (
                            <div className="attribute-row-price">{attr.price} por porción</div>
                        )}
                    </div>
                    <div className="quantity-controls-inline">
                        <button
                            className="qty-btn-inline minus"
                            onClick={() => handleDecrement(attr.id, attr.min_qty)}
                            disabled={qty <= 0}
                        >
                            −
                        </button>
                        <div className="qty-display-inline">{qty}</div>
                        <button
                            className="qty-btn-inline plus"
                            onClick={() => handleIncrement(attr.id, attr.max_qty, categoryData.category_id)}
                        >
                            +
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div key={categoryName} className="accordion-section">
                <button
                    className={`accordion-header ${hasError ? 'error' : ''}`}
                    onClick={() => toggleSection(categoryName)}
                >
                    <div className="accordion-header-content">
                        <span className="accordion-title">
                            {categoryName}
                            {minRequired > 0 && <span className="required-indicator">*</span>}
                        </span>
                        <span className="accordion-selected">{getSelectedLabel(categoryData)}</span>
                    </div>
                    <svg
                        className={`accordion-icon ${isExpanded ? 'expanded' : ''}`}
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <path
                            d="M6 9L12 15L18 9"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>

                {hasError && (
                    <div className="error-banner">{hasError}</div>
                )}

                {isExpanded && (
                    <div className="accordion-content">
                        {isSingleSelection ? (
                            <div className="options-list">
                                {categoryData.attributes.map(attr => {
                                    const qty = selectedQuantities[attr.id] || 0;
                                    const isSelected = qty > 0;

                                    return (
                                        <button
                                            key={attr.id}
                                            className={`option-item ${isSelected ? 'selected' : ''}`}
                                            onClick={() => handleSelectSingle(attr.id, categoryData.category_id, categoryData)}
                                        >
                                            <div className="option-checkbox">
                                                {isSelected && (
                                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                        <path d="M16.6667 5L7.5 14.1667L3.33334 10" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className="option-name">{attr.name}</span>
                                            {attr.price > 0 && (
                                                <span className="option-price">+${attr.price.toLocaleString()}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="options-list">
                                {categoryData.attributes.map(attr => {
                                    const qty = selectedQuantities[attr.id] || 0;

                                    return (
                                        <div key={attr.id} className="option-item-counter">
                                            <div className="option-item-info">
                                                <span className="option-name">{attr.name}</span>
                                                {attr.price > 0 && (
                                                    <span className="option-price-sub">{attr.price} por porción</span>
                                                )}
                                            </div>
                                            <div className="quantity-controls-inline">
                                                <button
                                                    className="qty-btn-inline minus"
                                                    onClick={() => handleDecrement(attr.id, attr.min_qty)}
                                                    disabled={qty <= 0}
                                                >
                                                    −
                                                </button>
                                                <div className="qty-display-inline">{qty}</div>
                                                <button
                                                    className="qty-btn-inline plus"
                                                    onClick={() => handleIncrement(attr.id, attr.max_qty, categoryData.category_id)}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const imageSrc = getImageSrc();

    const fullscreenContent = (
        <div className="fullscreen-container">
            <BrandLogo />
            <div className="fullscreen-header">
                <div className="header-pattern"></div>
                <div className="header-content">
                    <img src={logo} alt="GetIT" className="header-logo" />
                </div>
            </div>

            <div className="fullscreen-body">
                <div className="content-scroll">
                    <div className="product-header-info">
                        <div className="product-text-info">
                            <h3 className="product-title-large">{product.name}</h3>
                            <div className="product-base-price">
                                ${product.price.toLocaleString()} • sin modificación
                            </div>
                        </div>
                        {imageSrc && (
                            <img src={imageSrc} alt={product.name} className="product-image-bg" />
                        )}
                    </div>

                    <div className="attributes-container">
                        {Object.keys(groupedAttributes).map(categoryName =>
                            renderAttributeSection(categoryName, groupedAttributes[categoryName])
                        )}
                    </div>
                </div>
            </div>

            <div className="fullscreen-footer">
                <div className="footer-actions">
                    <button className="btn-cancel" onClick={onClose}>
                        Cancelar
                    </button>
                    <button className="btn-add-to-cart" onClick={handleAddToCart}>
                        Agregar al Carrito • ${totalPrice.toLocaleString()}
                    </button>
                </div>
            </div>

            <style>{`
                .fullscreen-container {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: #ffffff;
                    z-index: 9999;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                }

                .fullscreen-header {
                    background: white;
                    padding: 1.5rem 2rem;
                    position: relative;
                    overflow: hidden;
                    flex-shrink: 0;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                }

                .header-pattern {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    opacity: 0.1;
                }

                .header-content {
                    position: relative;
                    z-index: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    max-width: 1400px;
                    margin: 0 auto;
                }

                .header-logo {
                    height: 80px;
                    width: auto;
                }

                .fullscreen-body {
                    flex: 1;
                    overflow: hidden;
                    background: #f5f5f5;
                }

                .content-scroll {
                    height: 100%;
                    overflow-y: auto;
                    padding: 3rem;
                }

                .product-header-info {
                    max-width: 1000px;
                    margin: 0 auto 3rem;
                    background: white;
                    border-radius: 20px;
                    padding: 2.5rem;
                    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 2rem;
                    position: relative;
                    min-height: 180px;
                }

                .product-text-info {
                    flex: 1;
                    z-index: 2;
                }

                .product-title-large {
                    font-size: 2.5rem;
                    font-weight: 900;
                    color: #1a1a1a;
                    margin: 0 0 1rem 0;
                    line-height: 1.2;
                }

                .product-base-price {
                    font-size: 1.375rem;
                    color: #6b7280;
                    font-weight: 600;
                }

                .product-image-bg {
                    width: 200px;
                    height: 200px;
                    object-fit: cover;
                    border-radius: 16px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    flex-shrink: 0;
                    z-index: 1;
                }

                .attributes-container {
                    max-width: 1000px;
                    margin: 0 auto;
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }

                .attribute-row-direct {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 1.75rem 2rem;
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                }

                .attribute-row-info {
                    flex: 1;
                }

                .attribute-row-name {
                    font-size: 1.375rem;
                    font-weight: 700;
                    color: #1a1a1a;
                    margin-bottom: 0.25rem;
                }

                .attribute-row-price {
                    font-size: 1rem;
                    color: #6b7280;
                    font-weight: 600;
                }

                .accordion-section {
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                    overflow: hidden;
                    border-left: 3px solid #e5e7eb;
                    transition: all 0.2s;
                }

                .accordion-section:hover {
                    border-left-color: #10a37f;
                }

                .accordion-header {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 1.75rem 2rem;
                    background: white;
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s;
                    text-align: left;
                }

                .accordion-header:hover {
                    background: #f0fdf4;
                    border-left: 4px solid #0E8164;
                }

                .accordion-header.error {
                    background: #fef2f2;
                }

                .accordion-header-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .accordion-title {
                    font-size: 1.375rem;
                    font-weight: 700;
                    color: #1a1a1a;
                }

                .required-indicator {
                    color: #DC2626;
                    margin-left: 0.25rem;
                }

                .accordion-selected {
                    font-size: 1.125rem;
                    color: #DC2626;
                    font-weight: 600;
                }

                .accordion-icon {
                    color: #6b7280;
                    transition: transform 0.2s;
                    flex-shrink: 0;
                }

                .accordion-icon.expanded {
                    transform: rotate(180deg);
                }

                .error-banner {
                    padding: 1rem 2rem;
                    background: #fef2f2;
                    color: #DC2626;
                    font-size: 1.125rem;
                    font-weight: 600;
                    border-top: 2px solid #fee2e2;
                }

                .accordion-content {
                    padding: 1.5rem 2rem 2rem;
                    animation: expandDown 0.2s ease;
                    border-top: 2px solid #f3f4f6;
                }

                @keyframes expandDown {
                    from {
                        opacity: 0;
                        max-height: 0;
                    }
                    to {
                        opacity: 1;
                        max-height: 1000px;
                    }
                }

                .options-list {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .option-item {
                    display: flex;
                    align-items: center;
                    gap: 1.25rem;
                    padding: 1.25rem 1.5rem;
                    background: #f9fafb;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.2s;
                    width: 100%;
                    text-align: left;
                }

                .option-item:hover {
                    border-color: #0E8164;
                    background: #f0fdf4;
                }

                .option-item.selected {
                    border-color: #0E8164;
                    background: #0E8164;
                }

                .option-checkbox {
                    width: 32px;
                    height: 32px;
                    border-radius: 6px;
                    border: 3px solid #d1d5db;
                    background: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    transition: all 0.2s;
                }

                .option-item.selected .option-checkbox {
                    border-color: #0E8164;
                    background: #0E8164;
                }

                .option-name {
                    flex: 1;
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #1a1a1a;
                }

                .option-item.selected .option-name {
                    color: white;
                }

                .option-price {
                    font-size: 1.125rem;
                    font-weight: 700;
                    color: #0E8164;
                }

                .option-item.selected .option-price {
                    color: white;
                }

                .option-item-counter {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 1.25rem 1.5rem;
                    background: #f9fafb;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                }

                .option-item-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }

                .option-price-sub {
                    font-size: 1rem;
                    color: #6b7280;
                }

                .quantity-controls-inline {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .qty-btn-inline {
                    width: 48px;
                    height: 48px;
                    border-radius: 10px;
                    border: none;
                    background: #0E8164;
                    color: white;
                    font-size: 1.5rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .qty-btn-inline.minus {
                    background: #9ca3af;
                }

                .qty-btn-inline:hover:not(:disabled) {
                    transform: scale(1.1);
                }

                .qty-btn-inline:disabled {
                    background: #e5e7eb;
                    cursor: not-allowed;
                    opacity: 0.5;
                }

                .qty-display-inline {
                    min-width: 48px;
                    text-align: center;
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #1a1a1a;
                }

                .fullscreen-footer {
                    background: white;
                    padding: 1.75rem 2rem;
                    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
                    border-top: 3px solid #e5e7eb;
                    flex-shrink: 0;
                }

                .footer-actions {
                    display: flex;
                    gap: 1.5rem;
                    max-width: 1400px;
                    margin: 0 auto;
                    align-items: center;
                }

                .btn-cancel {
                    padding: 1.25rem 2.5rem;
                    font-size: 1.25rem;
                    font-weight: 700;
                    border: 3px solid #6b7280;
                    border-radius: 16px;
                    cursor: pointer;
                    transition: all 200ms ease;
                    background: white;
                    color: #6b7280;
                }

                .btn-cancel:hover {
                    background: #6b7280;
                    color: white;
                    transform: translateY(-2px);
                }

                .btn-add-to-cart {
                    flex: 1;
                    padding: 1.5rem 3rem;
                    font-size: 1.5rem;
                    font-weight: 800;
                    border: none;
                    border-radius: 16px;
                    cursor: pointer;
                    transition: all 200ms ease;
                    background: #FF9500;
                    color: white;
                    box-shadow: 0 4px 12px rgba(255, 149, 0, 0.3);
                }

                .btn-add-to-cart:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 20px rgba(255, 149, 0, 0.4);
                    background: #e68600;
                }

                @media (max-width: 768px) {
                    .content-scroll {
                        padding: 1.5rem;
                    }

                    .footer-actions {
                        flex-direction: column;
                        gap: 1rem;
                    }

                    .btn-cancel,
                    .btn-add-to-cart {
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );

    return createPortal(fullscreenContent, document.body);
};

export default ProductOptionsModal;